import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomDropdown } from 'src/components';
import { type MembershipRole, MembershipRoleEnum } from 'src/types';

type Props = {
  showDropdown: boolean;
  handleShowDropdown: () => void;
  isPending: boolean;
  displayPending: boolean;
  role: MembershipRole;
  handleSelectRole?: (role: MembershipRole, id: string) => void;
  dropdownId: string;
  userId: string;
  disableDropdown?: boolean;
  excludeDropdownSpace?: boolean; // used for the kick user modal
  showMemberBorder?: boolean; // used for the kick user modal
};

// TODO - create a separate component called 'RoleBadge' that this component will wrap.
// TODO - add a loading spinner to the dropdown when update is pending
// TODO - add tooltip to the dropdown if the 'disableDropdown' prop is true
//   - meaning the user cannot change their own role
export const RoleDropdown = ({
  showDropdown,
  handleShowDropdown,
  isPending,
  displayPending,
  role,
  handleSelectRole,
  dropdownId,
  userId,
  disableDropdown = false,
  excludeDropdownSpace = false,
  showMemberBorder = false,
}: Props) => {
  const anchorEl = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();

  return (
    <div
      ref={anchorEl}
      onClick={() => !disableDropdown && handleShowDropdown()}
      className={`group flex h-8 flex-row flex-nowrap items-center justify-between rounded-[4px] py-2 pl-4 pr-3 transition ${
        excludeDropdownSpace
          ? 'w-fit'
          : 'w-[120px] min-w-[120px] max-w-[120px] hover:cursor-pointer'
      } ${showDropdown ? 'ring-1 ring-primary' : 'ring-0'} ${
        isPending && displayPending
          ? 'border-[1.5px] border-dashed border-[#697786] bg-transparent hover:border-[#697786]/60'
          : role === 'admin'
            ? 'bg-[#D3E0FF] hover:bg-[#D3E0FF]/80'
            : role === 'approver'
              ? 'bg-[#FFE9AF] hover:bg-[#FFE9AF]/70'
              : role === 'member'
                ? `bg-cloudGray hover:bg-cloudGray/70 ${
                    showMemberBorder ? 'border border-[#505964]' : ''
                  }`
                : ''
      }`}
    >
      <span
        className={`text-semibold whitespace-nowrap font-secondary text-sm leading-[14px] transition ${
          isPending && displayPending
            ? 'text-[#697786] group-hover:text-[#697786]/60'
            : role === 'admin'
              ? 'text-[#425FA9]'
              : role === 'approver'
                ? 'text-[#78683D]'
                : role === 'member'
                  ? 'text-[#505964]'
                  : ''
        }`}
      >
        {/* userRole will be a translation key */}
        {t('org_settings.user_role', {
          context: isPending ? 'pending' : role,
        })}
      </span>
      {!excludeDropdownSpace && (
        <>
          <KeyboardArrowUpIcon
            style={{
              display: disableDropdown ? 'none' : 'block',
              fontSize: '18px',
              transform: showDropdown ? 'rotate(0deg)' : 'rotate(180deg)',
              transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            className={`cursor-pointer ${
              isPending
                ? 'text-[#697786] group-hover:text-[#697786]/60'
                : role === 'admin'
                  ? 'text-[#425FA9]'
                  : role === 'approver'
                    ? 'text-[#78683D]'
                    : role === 'member'
                      ? 'text-[#505964]'
                      : ''
            }`}
          />
          <CustomDropdown
            id={dropdownId}
            anchorEl={anchorEl.current}
            open={showDropdown}
            handleClick={handleShowDropdown}
            customZIndex={1300}
          >
            <div className='flex w-[269px] min-w-[269px] max-w-[269px] flex-col items-center justify-start gap-4 rounded-lg bg-cloudGray p-6'>
              <h5 className='heading-100 w-full text-primary-dark'>
                Change Role:
              </h5>

              {Object.keys(MembershipRoleEnum)
                .reverse()
                .map((memRole) => {
                  return (
                    <div
                      key={memRole}
                      onClick={() =>
                        handleSelectRole
                          ? handleSelectRole(memRole as MembershipRole, userId)
                          : console.error(
                              'RoleDropdown: handleSelectRole function is not defined',
                            )
                      }
                      className='group flex w-full flex-row flex-nowrap items-center justify-start gap-4 hover:cursor-pointer'
                    >
                      <input
                        type='checkbox'
                        value={memRole}
                        defaultChecked={role === memRole}
                        className='green-checkbox rounded-full bg-offWhite group-hover:cursor-pointer'
                      />
                      <div className='flex flex-col items-start justify-center gap-1'>
                        <label
                          htmlFor={memRole}
                          className='heading-100 w-auto text-left group-hover:cursor-pointer'
                        >
                          {t('org_settings.user_role', {
                            context: memRole,
                          })}
                        </label>
                        <span className='font-secondary text-xs font-normal leading-[18px] text-primary-dark group-hover:cursor-pointer'>
                          {t(`org_settings.user_role_desc`, {
                            context: memRole,
                          })}
                        </span>
                      </div>
                    </div>
                  );
                })}
            </div>
          </CustomDropdown>
        </>
      )}
    </div>
  );
};
